import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ICFDIsZIPUploadDetail,
  ICFDIsZIPUploadInfo,
  TCFDIsZIPUploadCategories,
} from 'models/invoices.models'
import { CFDIS_ZIP_UPLOAD_CATEGORIES } from 'lib/enums/invoices.enums'
import { IUseDataGridServerPagination } from 'hooks/Shared/useDataGridServerPagination'
import useDialog from 'hooks/Shared/useDialog'
import useGetCFDIsZIPUploadDetails from 'hooks/Invoices/CFDIs/useGetCFDIsZIPUploadDetails'
import useGetCFDIsZIPUploadInfo from 'hooks/Invoices/CFDIs/useGetCFDIsZIPUploadInfo'
import { useAppDispatch } from 'store/hooks'
import { setExternalLinkURL } from 'store/SharedData/sharedData.reducer'

interface IUseCFDIsZIPUploadDetailsContainer {
  hasAtLeastOneCategoryData: boolean
  cfdisZIPUploadCategoriesDataMap: Record<
    TCFDIsZIPUploadCategories,
    Omit<IUseDataGridServerPagination<ICFDIsZIPUploadDetail>, 'refetch'>
  >
  cfdisZIPUploadInfo: ICFDIsZIPUploadInfo | undefined
  cfdisZIPUploadInfoLoading: boolean
  isCFDIsZIPUploadInfoError: boolean
  uploadDetailsError: boolean
  uploadDetailsLoading: boolean
}

const useCFDIsZIPUploadDetailsContainer =
  (): IUseCFDIsZIPUploadDetailsContainer => {
    const [params] = useSearchParams()

    const workspaceId = params.get('ws')
    const jobId = params.get('id')

    const dispatch = useAppDispatch()

    const dialog = useDialog()

    const {
      data: cfdisZIPUploadInfo,
      isError: isCFDIsZIPUploadInfoError,
      isFetching: isCFDIsZIPUploadInfoFetching,
      isLoading: isCFDIsZIPUploadInfoLoading,
    } = useGetCFDIsZIPUploadInfo({
      workspaceId,
      jobId,
    })

    const {
      refetch: fetchCFDIsZIPUploadSuccessDetails,
      ...fetchCFDIsZIPUploadSuccessDetailsPaginatedData
    } = useGetCFDIsZIPUploadDetails(
      CFDIS_ZIP_UPLOAD_CATEGORIES.SUCCESS,
      workspaceId,
      jobId
    )

    const {
      refetch: fetchCFDIsZIPUploadDuplicatedDetails,
      ...fetchCFDIsZIPUploadDuplicatedDetailsPaginatedData
    } = useGetCFDIsZIPUploadDetails(
      CFDIS_ZIP_UPLOAD_CATEGORIES.DUPLICATED,
      workspaceId,
      jobId
    )

    const {
      refetch: fetchCFDIsZIPUploadFormatErrorDetails,
      ...fetchCFDIsZIPUploadFormatErrorDetailsPaginatedData
    } = useGetCFDIsZIPUploadDetails(
      CFDIS_ZIP_UPLOAD_CATEGORIES.FORMAT_ERROR,
      workspaceId,
      jobId
    )

    const {
      refetch: fetchCFDIsZIPUploadGenericErrorDetails,
      ...fetchCFDIsZIPUploadGenericErrorDetailsPaginatedData
    } = useGetCFDIsZIPUploadDetails(
      CFDIS_ZIP_UPLOAD_CATEGORIES.GENERIC_ERROR,
      workspaceId,
      jobId
    )

    const cfdisZIPUploadInfoLoading =
      isCFDIsZIPUploadInfoFetching || isCFDIsZIPUploadInfoLoading

    const cfdisZIPUploadCategoriesDataMap = {
      [CFDIS_ZIP_UPLOAD_CATEGORIES.SUCCESS]:
        fetchCFDIsZIPUploadSuccessDetailsPaginatedData,
      [CFDIS_ZIP_UPLOAD_CATEGORIES.DUPLICATED]:
        fetchCFDIsZIPUploadDuplicatedDetailsPaginatedData,
      [CFDIS_ZIP_UPLOAD_CATEGORIES.FORMAT_ERROR]:
        fetchCFDIsZIPUploadFormatErrorDetailsPaginatedData,
      [CFDIS_ZIP_UPLOAD_CATEGORIES.GENERIC_ERROR]:
        fetchCFDIsZIPUploadGenericErrorDetailsPaginatedData,
    } as const

    const loadingStates = [
      fetchCFDIsZIPUploadSuccessDetailsPaginatedData.isLoading,
      fetchCFDIsZIPUploadDuplicatedDetailsPaginatedData.isLoading,
      fetchCFDIsZIPUploadFormatErrorDetailsPaginatedData.isLoading,
      fetchCFDIsZIPUploadGenericErrorDetailsPaginatedData.isLoading,
    ]

    const errorStates = [
      fetchCFDIsZIPUploadSuccessDetailsPaginatedData.isError,
      fetchCFDIsZIPUploadDuplicatedDetailsPaginatedData.isError,
      fetchCFDIsZIPUploadFormatErrorDetailsPaginatedData.isError,
      fetchCFDIsZIPUploadGenericErrorDetailsPaginatedData.isError,
    ]

    const uploadDetailsLoading = loadingStates.every(Boolean)
    const uploadDetailsError = errorStates.every(Boolean)

    const hasAtLeastOneCategoryData = Object.values(
      cfdisZIPUploadCategoriesDataMap
    ).some(
      paginatedData => paginatedData?.rows && paginatedData.rows.length > 0
    )

    useEffect(() => {
      uploadDetailsError &&
        dialog.danger({
          subtitle:
            'Hubo un error al obtener los detalles de la carga del zip.',
          primaryButtonText: 'Reintentar',
          secondaryButtonText: 'Cancelar',
          onPrimaryClick: () => {
            fetchCFDIsZIPUploadSuccessDetails()
            fetchCFDIsZIPUploadDuplicatedDetails()
            fetchCFDIsZIPUploadFormatErrorDetails()
            fetchCFDIsZIPUploadGenericErrorDetails()
          },
        })
    }, [
      dialog,
      uploadDetailsError,
      fetchCFDIsZIPUploadSuccessDetails,
      fetchCFDIsZIPUploadDuplicatedDetails,
      fetchCFDIsZIPUploadFormatErrorDetails,
      fetchCFDIsZIPUploadGenericErrorDetails,
    ])

    useEffect(() => () => {
      dispatch(setExternalLinkURL(undefined))
    })

    return {
      hasAtLeastOneCategoryData,
      cfdisZIPUploadCategoriesDataMap,
      cfdisZIPUploadInfo,
      cfdisZIPUploadInfoLoading,
      isCFDIsZIPUploadInfoError,
      uploadDetailsError,
      uploadDetailsLoading,
    }
  }

export default useCFDIsZIPUploadDetailsContainer
